export type TipoCliente = 'persona' | 'empresa' | 'socio';

export type TipoDocumento = 'DNI' | 'CE' | 'PASSPORT' | 'RUC';

export type EstadoFiltro = '' | 'activo' | 'inactivo';

export interface Cliente {
  id: string;
  tipo: TipoCliente;
  tipoDocumento: TipoDocumento;
  numeroDocumento: string;
  nombres: string;
  apellidos: string;
  fechaNacimiento: string | null;
  telefono: string;
  correo: string;
  direccion: string;
  codigo: string;
  distrito: string;
  activo: boolean;
  membresia: Membresia | null;
}

export type ClienteFormValue = Omit<Cliente, 'id' | 'membresia'>;

export interface Membresia {
  cuentaId: string;
  usuario: string;
  estadoId: string;
  estado: string;
  activa: boolean;
  socioDesde: string | null;
  sincronizado: string;
}

export interface CuentaInclub {
  numeroDocumento: string;
  nombres: string;
  apellidos: string;
  telefono: string;
  usuario: string;
  estado: string;
  activa: boolean;
  socioDesde: string | null;
  clienteRegistrado: Cliente | null;
  yaEsSocio: boolean;
}

export interface ConsultaClientes {
  tipo: TipoCliente;
  estado: EstadoFiltro;
  busqueda: string;
  pagina: number;
  tamano: number;
}

export interface PaginaClientes {
  items: Cliente[];
  pagina: number;
  tamano: number;
  total: number;
  totalPaginas: number;
}

export const PAGINA_VACIA: PaginaClientes = { items: [], pagina: 0, tamano: 0, total: 0, totalPaginas: 0 };

export interface ResultadoImportacion {
  creados: number;
  actualizados: number;
  errores: { fila: number; mensaje: string }[];
}

export function admiteImportacion(tipo: TipoCliente): boolean {
  return tipo !== 'socio';
}

export interface OpcionDocumento {
  valor: TipoDocumento;
  etiqueta: string;
  longitud: number;
  exacta: boolean;
  soloDigitos: boolean;
}

const DOCUMENTOS_PERSONA: OpcionDocumento[] = [
  { valor: 'DNI', etiqueta: 'DNI', longitud: 8, exacta: true, soloDigitos: true },
  { valor: 'CE', etiqueta: 'CE', longitud: 12, exacta: false, soloDigitos: false },
  { valor: 'PASSPORT', etiqueta: 'Pasaporte', longitud: 12, exacta: false, soloDigitos: false },
];

export const DOCUMENTOS_POR_TIPO: Record<TipoCliente, OpcionDocumento[]> = {
  persona: DOCUMENTOS_PERSONA,
  empresa: [{ valor: 'RUC', etiqueta: 'RUC', longitud: 11, exacta: true, soloDigitos: true }],
  socio: [DOCUMENTOS_PERSONA[0]],
};

export function opcionDocumento(tipo: TipoCliente, valor: TipoDocumento): OpcionDocumento {
  const opciones = DOCUMENTOS_POR_TIPO[tipo];
  return opciones.find((o) => o.valor === valor) ?? opciones[0];
}

const ETIQUETAS_TIPO: Record<TipoCliente, string> = {
  persona: 'Persona',
  empresa: 'Empresa',
  socio: 'Socio',
};

export function etiquetaTipoCliente(tipo: TipoCliente): string {
  return ETIQUETAS_TIPO[tipo];
}

export function esPersonaNatural(tipo: TipoCliente): boolean {
  return tipo !== 'empresa';
}

export function nombreCompleto(cliente: Pick<Cliente, 'nombres' | 'apellidos'>): string {
  return [cliente.nombres, cliente.apellidos].filter(Boolean).join(' ');
}

export function agruparTelefono(telefono: string): string {
  return telefono.replace(/\D/g, '').replace(/(\d{3})(?=\d)/g, '$1 ');
}

export function formatearTelefono(telefono: string): string {
  return telefono ? `+51 ${agruparTelefono(telefono)}` : '';
}

export function direccionCompleta(cliente: Pick<Cliente, 'direccion' | 'distrito'>): string {
  return [cliente.direccion, cliente.distrito, 'Perú'].filter(Boolean).join(', ');
}

export function iniciales(cliente: Pick<Cliente, 'nombres' | 'apellidos'>): string {
  return nombreCompleto(cliente)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join('');
}
